/**
 * Wire types — mirror server PROTOCOL.md §2 / §5.
 * Keep field names identical so reading FFR_ProChatExt.as side-by-side
 * remains 1:1.
 */

/** k-enum from FFR_ProChatExt.as:11-28. Authoritative on the server. */
export const K = {
    Banned:              0,
    Administrator:       1,
    GameManager:         2,
    GlobalModerator:     3,
    RetiredStaff:        4,
    ForumModerator:      5,
    ProfileModerator:    6,
    SectionalModerator:  7,
    Developer:           8,
    EventStaff:          9,
    SimfileJudge:       10,
    Difficulty:         11,
    Simfile:            12,
    Music:              13,
    Elite:              14,
    Veteran:            15,
    User:               16,
    Anon:               99,
} as const;
export type KEnum = typeof K[keyof typeof K];

export interface UserView {
    id: number;
    name: string;
    k: KEnum;
    color: string;
    is_mod: boolean;
    is_admin: boolean;
}

export interface UserListEntry extends UserView {
    uStatus: number;
    avatar: string;
}

export interface ChatMessage {
    /** Local-only id for React keying. */
    localId: number;
    /** 'say' | 'pm' | 'generic_mess' | 'user_mess' | 'blank_mess' | 'sound_message' | 'chat_cmd' | 'system' | 'error' */
    kind: string;
    room: string;
    from?: UserView;
    /** Recipient userid for 'pm' / 'user_mess'. */
    to_uid?: number;
    body: string;
    /** soundboard `board/clip` key for 'sound_message'. */
    sound_p?: string;
    /** absolute file the server resolved the sound key to. */
    sound_file?: string;
    /** unix seconds */
    t: number;
}

export type ServerFrame =
    | { evt: 'session'; me: UserView; avatar?: string; fresh_window: number; t: number }
    | { evt: 'user_join'; room: string; user: UserListEntry; t?: number }
    | { evt: 'user_leave'; room: string; uid: number; name: string; t: number }
    | { evt: 'userlist'; room: string; users: UserListEntry[] }
    | { evt: 'chat_state'; room: string; closed: boolean; until: number | null }
    | { evt: 'chat_opened'; room: string; by?: string; t: number }
    | { evt: 'chat_closed'; room: string; minutes: number | null; until: number | null; by?: string; t: number }
    | { evt: 'say'; room: string; from: UserView; m: string; t: number }
    | { evt: 'pm'; room: string; from: UserView; to: string; m: string; t: number }
    | { evt: 'generic_mess'; room: string; from: UserView; m: string; t: number }
    | { evt: 'user_mess'; room: string; to_uid: number; from: UserView; m: string; t: number }
    | { evt: 'blank_mess'; room: string; m: string; t: number }
    | { evt: 'sound_message'; room: string; from: UserView; p: string; file?: string; m: string; t: number }
    | { evt: 'chat_cmd'; room: string; from: UserView; c: string; t: number }
    | { evt: 'ping_user'; room: string; u: string; t: number }
    | { evt: 'slap_user'; room: string; u: string; t: number }
    | { evt: 'admin_ban'; room: string; uname: string; minutes: number | null; until: number | null; by: string; t: number }
    | { evt: 'admin_mute'; room: string; uname: string; minutes: number | null; until: number | null; by: string; t: number }
    | { evt: 'muted'; minutes: number; until: number | null }
    | { evt: 'user_status'; room: string; u: string; uid: number; s: number; t: number }
    | { evt: 'user_status_all'; room: string; s: number; t: number }
    | { evt: 'drop_bomb'; room: string; by: string; t: number }
    | { evt: 'walrus'; room: string; by: string; t: number }
    | { evt: 'walrus_bucket'; room: string; by: string; t: number }
    | { evt: 'now_playing'; title: string; artist: string; listeners: string; url?: string; t: number }
    | { evt: 'warning'; reason: string; m: string }
    // Connect 4 — you_play is 0|1 for players, null for spectators
    | { evt: 'c4_invite_sent'; game_id: string; to: UserView; ttl_ms: number; t: number }
    | { evt: 'c4_invite';      game_id: string; from: UserView; ttl_ms: number; t: number }
    | { evt: 'c4_decline';     game_id: string; by: string; reason?: string }
    | { evt: 'c4_start';       game_id: string; players: [string, string]; you_play: 0 | 1 | null; turn: 0 | 1; board: number[][]; room?: string; t: number }
    | { evt: 'c4_move';        game_id: string; col: number; row: number; by: 0 | 1; board: number[][]; turn: 0 | 1; status: string; you_play: 0 | 1 | null; room?: string; t: number }
    | { evt: 'c4_end';         game_id: string; status: 'ended'; reason: 'win' | 'draw' | 'resigned'; winner: 0 | 1 | null; players?: [string, string]; board: number[][]; you_play: 0 | 1 | null; room?: string; t: number }
    | { evt: 'c4_stats_result'; user: string | number | null; uid: number; wins: number; losses: number; draws: number; resigned_for: number; resigned_against: number; total: number; unknown: boolean; t: number }
    | { res: 'u_count'; req_id?: string; rooms: Array<{ name: string; c: number }> }
    | { res: 'client_ping'; req_id?: string; t: number }
    | { res: 'chat_topic'; req_id?: string; room: string; topic: string; set_by: string; set_at: number }
    | { evt: 'chat_topic'; room: string; topic: string; set_by: string; set_at: number }
    | { res: 'get_chat'; req_id?: string; room: string; room_id: number; max_users: number; is_private: boolean }
    | { res: 'join_chat'; req_id?: string; room: string }
    | { err: string; until?: number | null; reason?: string; detail?: string; retry_after?: number; cmd?: string };
